import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { invigilationApi, isoDate, roleLabel, SHIFTS } from '../lib/invigilationApi';
import type { AssignmentView, InvigilationDay, PlanOptions, RoomView } from '../lib/invigilationApi';
import { StatTile } from '../components/charts';

/**
 * THE INVIGILATION CHART — who sits in which room, worked out instead of written on the back of
 * the exam timetable at nine o'clock the night before.
 *
 * ⚠ WHAT THIS SCREEN IS FOR IS THE COMPROMISE COLUMN, NOT THE NAMES. Any allocator can produce a
 * list of names. What a head of school actually needs to know is which of those names broke a rule
 * and which rule — because that is the one they will be asked about. Every bent rule is printed in
 * words on the row that bent it.
 *
 * ⚠ A PROPOSAL IS NOT A CHART. Nothing is saved until Publish, and staff cannot see a draft. The
 * banner says which state the screen is in, because "I thought I'd published it" is how a room ends
 * up unstaffed.
 */
export function InvigilationPage() {
  const [opts, setOpts] = useState<PlanOptions>(() => ({
    institutionId: null,
    date: isoDate(new Date()),
    shift: 'Morning',
    relieving: 2,
    squad: 0,
    maxDuties: 6,
    avoidOwnSubject: true,
    avoidOwnClass: true,
  }));

  const [d, setD] = useState<InvigilationDay | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState<'' | 'publish' | 'clear' | 'plan'>('');

  /* ⚠ A TEACHER CAN BUILD THE CHART AND CANNOT PUBLISH IT, AND THE BUTTONS HAVE TO SAY SO.
   *
   * The exam in-charge at most schools is a senior teacher, not the HR manager — so planning is
   * open to `teaching`. Publishing puts duties on a noticeboard and commits colleagues to being in
   * a room at nine in the morning; that stays with the head. The API enforces both.
   *
   * Rendering the publish button anyway would 403 on click, which is exactly the "shows people
   * buttons that 403" failure the feature-guard comment warns about — and worse here, because the
   * teacher would reasonably believe the chart had gone up. */
  const { user } = useAuth();
  const canPublish = ['TrustAdmin', 'CampusAdmin', 'Principal', 'HrManager'].includes(user?.role ?? '');
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback((o: PlanOptions, regenerate: boolean) => {
    setLoading(true);
    return invigilationApi
      .day(o, regenerate)
      .then((r) => {
        setD(r);
        setFailed(false);
        // The server picks the institution when the caller did not; hold on to its choice so the
        // next request does not silently drift back to the first one in the list.
        if (!o.institutionId && r.institutionId) setOpts((p) => ({ ...p, institutionId: r.institutionId }));
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let alive = true;
    invigilationApi
      .day(opts, false)
      .then((r) => {
        if (!alive) return;
        setD(r);
        setFailed(false);
        if (!opts.institutionId && r.institutionId) setOpts((p) => ({ ...p, institutionId: r.institutionId }));
      })
      .catch(() => alive && setFailed(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.institutionId, opts.date, opts.shift]);

  const set = <K extends keyof PlanOptions>(k: K, v: PlanOptions[K]) =>
    setOpts((p) => ({ ...p, [k]: v }));

  const replan = () => {
    setBusy('plan');
    setFlash(null);
    load(opts, true).finally(() => setBusy(''));
  };

  const publish = () => {
    setBusy('publish');
    invigilationApi
      .publish(opts)
      .then((r) => {
        setFlash(`Published — ${r.published} duties are now on the noticeboard.`);
        return load(opts, false);
      })
      .catch(() => setFlash('Could not publish the chart.'))
      .finally(() => setBusy(''));
  };

  const clear = () => {
    setBusy('clear');
    invigilationApi
      .clear(opts.institutionId ?? null, opts.date, opts.shift)
      .then((r) => {
        setFlash(`Withdrawn — ${r.cleared} duties removed from the noticeboard.`);
        return load(opts, true);
      })
      .catch(() => setFlash('Could not withdraw the chart.'))
      .finally(() => setBusy(''));
  };

  const byRoom = useMemo(() => groupByRoom(d?.assignments ?? [], d?.rooms ?? []), [d]);

  if (loading && !d) return <p style={{ color: 'var(--ink-3)' }}>Working out the invigilation chart…</p>;
  /**
   * ⚠ SHAPE, NOT NULLNESS — and this crashed before the check was written this way.
   *
   * The guard was `!d`. An endpoint that answers `{{}}` — a proxy, a stub, a bad deploy, a
   * gateway error page, a route that matched more broadly than intended — returns a TRUTHY object
   * with none of the fields this screen reads, so the guard passed and the render died on
   * `d.papers.length`. Every page sweep run found this same fault on four different screens; it is not a
   * fixture artefact, it is what a bad morning looks like.
   *
   * Checking the one field the screen cannot work without turns a stack trace into an empty state.
   */
  if (failed || !Array.isArray(d?.papers) || !Array.isArray(d?.assignments)) {
    return <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>Could not load the invigilation chart.</div>;
  }

  const noPapers = d.papers.length === 0;

  return (
    <div className="inv">
      <header className="inv-head">
        <div>
          <h1>Invigilation</h1>
          <p className="inv-sub">
            Who sits in which room — allocated so that nobody is in two rooms at once, nobody on
            leave is on the chart, and the load is spread.
          </p>
        </div>
      </header>

      {/* ---------------------------------------------------------------- controls */}
      <div className="card inv-controls">
        <label>
          <span>Institution</span>
          <select
            value={opts.institutionId ?? ''}
            onChange={(e) => set('institutionId', e.target.value || null)}
          >
            {d.institutions.map((i) => (
              <option key={i.id} value={i.id}>{i.name}</option>
            ))}
          </select>
        </label>

        <label>
          <span>Date</span>
          <input type="date" value={opts.date} onChange={(e) => set('date', e.target.value)} />
        </label>

        <label>
          <span>Sitting</span>
          <select value={opts.shift} onChange={(e) => set('shift', e.target.value)}>
            {SHIFTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>

        <label>
          <span>Relieving</span>
          <input
            type="number" min={0} max={20} value={opts.relieving}
            onChange={(e) => set('relieving', clamp(e.target.value, 0, 20))}
          />
        </label>

        <label>
          <span>Squad</span>
          <input
            type="number" min={0} max={10} value={opts.squad}
            onChange={(e) => set('squad', clamp(e.target.value, 0, 10))}
          />
        </label>

        <label title="A cap, not a target. It is relaxed automatically — and visibly — when a room would otherwise go unstaffed.">
          <span>Fair-share cap</span>
          <input
            type="number" min={0} max={50} value={opts.maxDuties}
            onChange={(e) => set('maxDuties', clamp(e.target.value, 0, 50))}
          />
        </label>

        <div className="inv-toggles">
          <label className="inv-check">
            <input
              type="checkbox" checked={opts.avoidOwnSubject}
              onChange={(e) => set('avoidOwnSubject', e.target.checked)}
            />
            <span>Not their own subject</span>
          </label>
          <label className="inv-check">
            <input
              type="checkbox" checked={opts.avoidOwnClass}
              onChange={(e) => set('avoidOwnClass', e.target.checked)}
            />
            <span>Not their own class</span>
          </label>
        </div>

        <div className="inv-actions">
          <button className="btn" onClick={replan} disabled={busy !== '' || noPapers}>
            {busy === 'plan' ? 'Planning…' : d.isSaved ? 'Re-plan' : 'Plan again'}
          </button>
          {canPublish && (
            <button className="btn btn-primary" onClick={publish} disabled={busy !== '' || noPapers || d.assignments.length === 0}>
              {busy === 'publish' ? 'Publishing…' : 'Publish chart'}
            </button>
          )}
          {canPublish && d.isSaved && (
            <button className="btn btn-quiet" onClick={clear} disabled={busy !== ''}>
              {busy === 'clear' ? 'Withdrawing…' : 'Withdraw'}
            </button>
          )}
          {/* ⚠ Says who does it, rather than leaving a teacher wondering where the button went. */}
          {!canPublish && (
            <span style={{ fontSize: 12.5, color: 'var(--ink-3)', alignSelf: 'center' }}>
              Plan it here, then ask the head of school to publish — putting duties on the
              noticeboard is theirs to sign off.
            </span>
          )}
        </div>
      </div>

      {flash && <div className="inv-flash">{flash}</div>}

      {/* ---------------------------------------------------------------- state banner */}
      <div className={`inv-state ${d.isSaved ? 'is-published' : 'is-draft'}`}>
        {d.isSaved
          ? 'Published. Staff can see their own duty in the app.'
          : 'Draft. Nothing is saved and no member of staff can see this yet.'}
      </div>

      {/* ⚠ Suppressed when the empty card below says the same thing in more detail. Two banners
          carrying one message trains people to skim past the banner that matters. */}
      {d.note && !noPapers && <div className="inv-note">{d.note}</div>}

      {/* ---------------------------------------------------------------- tiles */}
      <div className="inv-tiles">
        <StatTile label="Papers" value={String(d.papers.length)} foot={`${d.rooms.length} room${d.rooms.length === 1 ? '' : 's'} in use`} />
        <StatTile label="On duty" value={noPapers ? '\u2014' : String(d.assigned)} foot={`${d.candidatesAvailable} staff available`} />
        {/* ⚠ On a day with no papers these read "0 rules bent — a clean chart" and "0 unstaffed —
            every slot filled", which is a reassuring green light about a chart that does not
            exist. Nothing planned is not the same as nothing wrong. */}
        <StatTile
          label="Rules bent"
          value={noPapers ? '\u2014' : String(d.compromised)}
          foot={noPapers
            ? 'Nothing planned'
            : d.compromised === 0
              ? 'None \u2014 a clean chart'
              : <span className="inv-warn">Each one says which, on the row</span>}
        />
        <StatTile
          label="Unstaffed"
          value={noPapers ? '\u2014' : String(d.unfilled.length)}
          foot={noPapers
            ? 'Nothing planned'
            : d.unfilled.length === 0
              ? 'Every slot filled'
              : <span className="inv-bad">A room without an invigilator cannot open</span>}
        />
        <StatTile label="Unavailable" value={String(d.candidatesUnavailable)} foot="On leave, or left the school" />
        {/* \u26a0 minLoad/maxLoad COUNT THIS SHIFT'S ROWS PER PERSON \u2014 they are not the three-week
            fairness figures. InvigilationReader.Shape recomputes them from the rows it is about to
            return and drops the window-wide pair the planner worked out. The three-week numbers
            are the ones in `busiest`, which is why the fairness card below reads those instead. */}
        <StatTile
          label="Load spread"
          value={d.assigned === 0 ? '\u2014' : `${d.minLoad}\u2013${d.maxLoad}`}
          foot="Duties per person, this shift"
        />
      </div>

      {noPapers && (
        <div className="card inv-empty">
          <p><strong>No papers are scheduled on {pretty(d.date)}.</strong></p>
          <p className="inv-sub">
            There is nothing to invigilate. {d.examDates.length > 0
              ? 'Nearby dates that do have papers:'
              : 'No exams are scheduled in the next two months either.'}
          </p>
          {d.examDates.length > 0 && (
            <div className="inv-dates">
              {d.examDates.slice(0, 14).map((x) => (
                <button key={x} className="inv-datechip" onClick={() => set('date', x)}>{pretty(x)}</button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---------------------------------------------------------------- unfilled */}
      {d.unfilled.length > 0 && (
        <div className="card inv-unfilled">
          <h2>Could not be staffed</h2>
          <ul>
            {d.unfilled.map((u, i) => (
              <li key={i}>
                <strong>{u.roomName ?? roleLabel(u.role)}</strong>
                {u.roomName ? <> — {roleLabel(u.role)}</> : null}
                <span> · {u.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ---------------------------------------------------------------- the chart */}
      {byRoom.length > 0 && (
        <div className="inv-rooms">
          {byRoom.map((g) => {
            const room = d.rooms.find((r) => r.roomId === g.roomId);
            const short = shortfall(g, room);
            return (
              <div className={`card inv-room ${short > 0 ? 'is-short' : ''}`} key={g.key}>
                <div className="inv-room-head">
                  <h3>{g.roomName ?? 'Not tied to a room'}</h3>
                  {room && (
                    <span className="inv-room-meta">
                      {room.classLabel} · {room.subjectName} · {room.seated} seated of {room.capacity}
                    </span>
                  )}
                  {/* ⚠ ON THE ROOM, NOT IN A FOOTNOTE. This chart is printed and pinned up, and a
                      room staffed by one person where two were wanted otherwise looks exactly like
                      a full one — the shortfall was only findable by cross-reading the
                      "Could not be staffed" card, which nobody does at nine in the morning. */}
                  {short > 0 && (
                    <span className="inv-room-short">
                      {short} short — {staffed(g)} of {needed(room)} invigilators
                    </span>
                  )}
                </div>
                <ul className="inv-people">
                  {g.rows.length === 0 && (
                    <li className="inv-room-nobody">Nobody has been assigned to this room.</li>
                  )}
                  {g.rows.map((a, i) => (
                    <li key={a.employeeId + i} className={a.compromise ? 'is-compromised' : ''}>
                      <div className="inv-person">
                        <span className="inv-name">{a.fullName}</span>
                        {/* Only present when a namesake is on the same chart — see AssignmentView.Code. */}
                        {a.code && <span className="inv-code">{a.code}</span>}
                        <span className={`inv-role role-${a.role.toLowerCase()}`}>{roleLabel(a.role)}</span>
                        {a.designation && <span className="inv-desig">{a.designation}</span>}
                      </div>
                      {/* ⚠ Never hidden behind a tooltip. This is the sentence the head is asked about. */}
                      {a.compromise && <p className="inv-compromise">{a.compromise}</p>}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {/* ---------------------------------------------------------------- papers */}
      {d.papers.length > 0 && (
        <div className="card inv-papers">
          <h2>Papers on {pretty(d.date)} · {d.shift}</h2>
          <table className="inv-table">
            <thead>
              <tr><th>Class</th><th>Subject</th><th>Paper</th><th className="num">Sitting</th></tr>
            </thead>
            <tbody>
              {d.papers.map((p, i) => (
                <tr key={i}>
                  <td>{p.classLabel}</td>
                  <td>{p.subjectName}</td>
                  <td className="muted">{p.examName}</td>
                  <td className="num">{p.sitting || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {d.seatShortfall > 0 && (
            <p className="inv-bad">
              {d.seatShortfall} student{d.seatShortfall === 1 ? '' : 's'} have a paper and no seat.
              Every free room is already allocated.
            </p>
          )}
        </div>
      )}

      {/* ---------------------------------------------------------------- fairness */}
      {d.busiest.length > 0 && (
        <div className="card inv-busiest">
          <h2>Carrying the most</h2>
          <p className="inv-sub">
            Duties across the three weeks either side of this date, including this shift. This is
            the number that quietly goes wrong when a chart is written by hand.
          </p>
          {/* ⚠ A list of identical 1s is not information. When the load is perfectly flat the
              interesting fact is that it IS flat, so say that in a sentence instead of printing
              ten rows the eye has to check one by one.
              ⚠ Tested on `busiest` — which IS the three-week count this card is about — and not on
              maxLoad, which counts only this shift and can never exceed 1 (nobody may be placed
              twice in one shift), so gating on it hid the list on every chart ever planned. */}
          {d.busiest.every((b) => b.duties <= 1) ? (
            <p className="inv-flat">
              Perfectly level — every one of the {d.assigned} people on duty is carrying exactly
              one. Nobody is doubled up and nobody was missed.
            </p>
          ) : (
            <ul>
              {d.busiest.map((b) => (
                <li key={b.employeeId}>
                  <span>{b.fullName}</span>
                  <span className="inv-count">{b.duties}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------- helpers

interface RoomGroup { key: string; roomId: string | null; roomName: string | null; rows: AssignmentView[] }

/**
 * ⚠ Grouped in ONE pass with a Map, not by filtering the array once per room. Ninety-four staff
 * across twenty rooms is small, but the quadratic version is the same amount of code and is the
 * reason screens like this crawl once a college with four hundred staff opens them.
 *
 * ⚠ SEEDED FROM THE ROOMS IN USE, not only from the assignments. A room nobody could be staffed
 * for has no assignment rows, so grouping the rows alone made it vanish from the chart altogether
 * — the one room the school most needs to see.
 *
 * ⚠ Roving duty — squad and relieving — has no room, and is deliberately put LAST rather than
 * dropped. A reserve nobody printed is a reserve nobody turns up for.
 */
function groupByRoom(rows: AssignmentView[], rooms: RoomView[]): RoomGroup[] {
  const map = new Map<string, RoomGroup>();
  for (const r of rooms) map.set(r.roomId, { key: r.roomId, roomId: r.roomId, roomName: r.roomName, rows: [] });
  for (const r of rows) {
    const key = r.roomId ?? '~roving';
    let g = map.get(key);
    if (!g) {
      g = { key, roomId: r.roomId, roomName: r.roomName, rows: [] };
      map.set(key, g);
    }
    g.rows.push(r);
  }
  const out = [...map.values()];
  out.sort((a, b) => {
    if (a.roomId === null) return b.roomId === null ? 0 : 1;
    if (b.roomId === null) return -1;
    const x = roomKey(a.roomName);
    const y = roomKey(b.roomName);
    if (x.numbered !== y.numbered) return x.numbered ? -1 : 1;
    if (x.numbered && x.number !== y.number) return x.number - y.number;
    return x.name.localeCompare(y.name, undefined, { numeric: true });
  });
  for (const g of out) g.rows.sort((x, y) => rank(x.role) - rank(y.role));
  return out;
}

/**
 * ⚠ NUMBERED ROOMS LEAD, IN NUMERIC ORDER — halls and labs follow, alphabetically.
 *
 * A school reads its numbered rooms first and walks them in corridor order; plain alphabetical put
 * Assembly Hall and three labs above Room 12, and string ordering put Room 12 above Room 2. Only a
 * bare number, optionally behind a generic word, counts as numbered: "Computer Lab 1" is the name
 * of a lab, not room number one, and belongs with the other labs.
 */
const NUMBERED_ROOM = /^(?:room|rm|hall|block|class(?:room)?)?[\s.#-]*(\d+)\s*[a-z]?$/i;

function roomKey(name: string | null): { numbered: boolean; number: number; name: string } {
  const n = (name ?? '').trim();
  const m = NUMBERED_ROOM.exec(n);
  return { numbered: m !== null, number: m ? Number(m[1]) : 0, name: n };
}

/** Rooms carry a target; a room with none still needs one person in it. */
function needed(room: RoomView | undefined): number {
  return Math.max(1, room?.invigilatorsNeeded ?? 1);
}

/** ⚠ Relieving and squad are a reserve for the whole shift, not staff standing in this room. */
function staffed(g: RoomGroup): number {
  return g.rows.filter((r) => r.role === 'RoomInCharge' || r.role === 'Invigilator').length;
}

function shortfall(g: RoomGroup, room: RoomView | undefined): number {
  if (g.roomId === null) return 0;
  return Math.max(0, needed(room) - staffed(g));
}

function rank(role: string): number {
  return role === 'RoomInCharge' ? 0 : role === 'Invigilator' ? 1 : role === 'Relieving' ? 2 : 3;
}

function clamp(v: string, lo: number, hi: number): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return lo;
  return Math.min(hi, Math.max(lo, Math.round(n)));
}

/** ⚠ Parsed with an explicit local midnight. A bare `new Date('2026-08-09')` is UTC. */
function pretty(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
}
